import intersection from 'lodash/intersection';
import startCase from 'lodash/startCase';
import { arrayOf, bool, func, number, string } from 'prop-types';
import React, { Component } from 'react';
import { Button, ButtonGroup, Col, Row } from 'reactstrap';

import BulkToggleControls from '../../generic/BulkToggleControls';
import { InfoTooltip } from '../../generic/Tooltip';
import withConfirm from '../../generic/with-confirm';
import { creativeShape } from '../../prop-types';
import { flash } from '../../utils/flash';
import getEventTargetValue from '../../utils/get-event-target-value';
import CreativesTable from '../CreativesTable';
import CreativesMultiDownload from './CreativesMultiDownload';
import EditorModal from './EditorModal';

const DeleteButtonWithConfirm = withConfirm(
  'Do you really want to delete the selected creatives and remove them from all campaign lines they are assigned to?',
)(Button);

class ListCreatives extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedCreatives: [],
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSelection = this.handleSelection.bind(this);
    this.handleToggleAll = this.handleToggleAll.bind(this);
    this.handleChangeSelectedCreatives =
      this.handleChangeSelectedCreatives.bind(this);
    this.handleDeleteSelectedCreatives =
      this.handleDeleteSelectedCreatives.bind(this);
    this.handleFilter = this.handleFilter.bind(this);
    this.resetSelection = this.resetSelection.bind(this);
    this.tableRef = React.createRef();
  }

  handleChange(event) {
    const { name } = event.target;
    const value = getEventTargetValue(event.target);
    const [id, prop] = name.split('.');

    this.props.onUpdate(id, { [prop]: value });
  }

  handleChangeSelectedCreatives(name, value) {
    const { selectedCreatives } = this.state;

    selectedCreatives.map(id => this.props.onUpdate(id, { [name]: value }));
    flash(`${startCase(name)} updated to ${value} for all selected creatives`);
  }

  handleDeleteSelectedCreatives() {
    const { selectedCreatives } = this.state;
    selectedCreatives.forEach(id => this.props.onDelete(id));
    this.resetSelection();
  }

  handleSelection(id, e) {
    const selected = e.target.checked;
    this.setState(({ selectedCreatives }) => ({
      selectedCreatives: selected
        ? selectedCreatives.concat(id)
        : selectedCreatives.filter(ea => ea !== id),
    }));
  }

  handleToggleAll(checked) {
    const visibleCreatives = this.tableRef.current
      .getStateOrProp('sortedData')
      .map(row => row._original.id); // eslint-disable-line no-underscore-dangle
    const selectedCreatives = checked ? visibleCreatives : [];
    this.setState({ selectedCreatives });
  }

  handleFilter() {
    this.setState(({ selectedCreatives }) => {
      const visibleCreatives = this.tableRef.current
        .getStateOrProp('sortedData')
        .map(row => row._original.id); // eslint-disable-line no-underscore-dangle
      return {
        selectedCreatives: intersection(selectedCreatives, visibleCreatives),
      };
    });
  }

  resetSelection() {
    this.setState({ selectedCreatives: [] });
  }

  render() {
    const {
      creatives,
      onDelete,
      resizeCreativeImage,
      campaignRelatedProducts,
      opportunityName,
      campaignId,
      onSuccess,
      campaignProductTags,
    } = this.props;

    const { selectedCreatives } = this.state;

    const noCreativeSelected = selectedCreatives.length === 0;
    const MultiDownloadTooltipText =
      'Make sure to select which creatives you want to download first.';
    return (
      <>
        <Row className="mb-2">
          <Col>
            <BulkToggleControls
              entities={creatives}
              activeEntities={selectedCreatives}
              onToggleAll={this.handleToggleAll}
            />
          </Col>
          <Col className="text-right">
            <ButtonGroup>
              <CreativesMultiDownload
                fileName={opportunityName}
                outline
                size="sm"
                creatives={selectedCreatives}
                className="mt-2"
              >
                Download Selected
                {!selectedCreatives.length && (
                  <span>
                    <InfoTooltip target="multiDownload-tooltip">
                      {MultiDownloadTooltipText}
                    </InfoTooltip>
                  </span>
                )}
              </CreativesMultiDownload>
              <div className="mt-2">
                <EditorModal
                  onChange={this.handleChangeSelectedCreatives}
                  onClose={this.resetSelection}
                  disabled={noCreativeSelected}
                  campaignProductTags={campaignProductTags}
                />
              </div>
              <DeleteButtonWithConfirm
                outline
                color="danger"
                size="sm"
                onClick={this.handleDeleteSelectedCreatives}
                disabled={noCreativeSelected}
                className="mt-2"
              >
                Delete selected
              </DeleteButtonWithConfirm>
            </ButtonGroup>
          </Col>
        </Row>
        <CreativesTable
          creatives={creatives}
          selectedCreatives={selectedCreatives}
          onSelection={this.handleSelection}
          onDeleteCreative={onDelete}
          resizeCreativeImage={resizeCreativeImage}
          onChange={this.handleChange}
          onFilter={this.handleFilter}
          tableRef={this.tableRef}
          campaignRelatedProducts={campaignRelatedProducts}
          onSuccess={onSuccess}
          campaignId={campaignId}
        />
      </>
    );
  }
}

ListCreatives.propTypes = {
  creatives: arrayOf(creativeShape.isRequired).isRequired,
  onDelete: func.isRequired,
  resizeCreativeImage: func.isRequired,
  onUpdate: func.isRequired,
  campaignRelatedProducts: bool.isRequired,
  opportunityName: string.isRequired,
  onSuccess: func.isRequired,
  campaignProductTags: arrayOf(string),
  campaignId: number,
};

ListCreatives.defaultProps = {
  campaignProductTags: [],
  campaignId: null,
};

export default ListCreatives;
